import { decode } from 'html-entities';
import React, { useEffect, useMemo, useState } from 'react';
import * as Icon from 'react-feather';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/redux';
import { store } from '../../store';

import {
    Button as AlchemyButton,
    Icon as AlchemyIcon,
    Tag as AlchemyTag
} from '@amzn/alchemy-components-react'

import {
    showViewBookmark,
    loadViewBookmark
} from '../../slices/bookmarkModalSlice';
import {itemTypes} from '../../enums/ItemTypes.enum';
import i18n from "../../i18n";
import { RootState } from '../../types';
import { Bookmark } from '../../types/Bookmark.type';
import AddFavoriteButton from '../Buttons/AddFavoriteButton';
import EditBookmarkButton from '../Buttons/EditBookmarkButton';
import RemoveBookmarkButton from '../Buttons/RemoveBookmarkButton';
import RemoveFavoriteButton from '../Buttons/RemoveFavoriteButton';
import { LoadingView } from '../Loading';

// feed bookmarks data to this component
type DataTableProps = {
    filteredItems: Array<Bookmark>
    itemType: any
    isGlobalSearching?: boolean
    actions?: React.ReactNode
}

export default function (props:DataTableProps) {
    const dispatch = useAppDispatch()
    const filteredItems = props.filteredItems
    const filteredItemsLength = filteredItems.length
    const actions = props.actions
    const isGlobalSearching = props.isGlobalSearching
    
    // Debug: Print component ID to track instances
    const componentId = React.useId();
    
    // We'll only use useSelector to get the loading state
    // This ensures we're always in sync with the Redux store
    const isLoading = useSelector((state: RootState) => {
        // console.log('DataTable: Current loading state from useSelector:', state.loading);
        return state.loading;
    });
    
    // No longer needed as we're not using localLoadingState anymore
    
    const mobileView = useSelector((state: RootState) => state.mobileView)
    // for table sorting
    const [sortConfig, setSortConfig] = useState({key: "title", direction: "asc"});
    const sortedData = useMemo(() => {
        let sortableData = [...filteredItems];
        if (sortConfig !== null) {
            sortableData.sort((a, b) => {
                const aValue = a[sortConfig.key as keyof Bookmark] || '';
                const bValue = b[sortConfig.key as keyof Bookmark] || '';
                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [filteredItems, sortConfig])

    // slice data into pages
    const listLoadNumber = 200
    const [list, setList] = useState<Bookmark[]>([])
    const [listIndex, setListIndex] = useState(listLoadNumber)

    useEffect(() => {
        setList(sortedData.slice(0, listLoadNumber))
        setListIndex(listLoadNumber)
    }, [sortedData])

    const handleList = () => {
        setList(prev => [...prev, ...sortedData.slice(listIndex, listIndex + listLoadNumber)]), [setList]
        setListIndex(prev => prev + listLoadNumber), [listIndex]
    }

    const handleExpandBookmark = (bookmark: Bookmark) => {
        dispatch(showViewBookmark())
        dispatch(loadViewBookmark(bookmark))
    }

    const requestSort = (key: keyof Bookmark) => {
        let direction = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }

    const itemType = props.itemType
    const removeButton = (bookmark: Bookmark) => {
        if (itemType === itemTypes.bookmarkCard) {
            return <RemoveBookmarkButton bookmark={bookmark}/>
        }
        if (itemType === itemTypes.myFavourites) {
            return <RemoveFavoriteButton favoritesType={itemTypes.myFavourites} bookmark={bookmark}/>
        }
        if (itemType === itemTypes.siteFavourites) {
            return <RemoveFavoriteButton favoritesType={itemTypes.siteFavourites} bookmark={bookmark}/>
        }
    }

    const emptyTableMsg = isGlobalSearching ? i18n.t('table:search-empty-message') : i18n.t('table:empty-message')

    // Add debugging for render conditions
    // useEffect(() => {
    //     console.log('DataTable: Render conditions:', { 
    //         isLoading, 
    //         listLength: list.length 
    //     });
    // }, [isLoading, list.length]);

    return (
        <div className='content bg-dark '>
            <div className="datatable">
                <div className="table-responsive">
                    <div className="datatable-header">
                        <div>{actions}</div>
                    </div>
                    <table className="bordered alchemy-table">
                        <thead>
                            <tr>
                                <th className="d-flex" onClick={() => requestSort('title')} >
                                    <div className="pr-1">{i18n.t("universal:title")}</div>
                                    {sortConfig.direction === "asc" ? <AlchemyIcon icon="sortAlphaUp" /> : <AlchemyIcon icon="sortAlphaDown" />}
                                </th>
                                { mobileView || <th style={{"width": "45%"}}>{i18n.t("universal:description")}</th> }
                                <th style={{"width": "10%"}}>{i18n.t("universal:actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading
                                ? (
                                      <tr><td colSpan={3}><span className='datatable-empty-content'>
                                        <LoadingView />
                                      </span></td></tr>
                                    )
                                : list.length === 0
                                    ? <tr><td colSpan={3}><span className='datatable-empty-content'>{emptyTableMsg}</span></td></tr>
                                    : list.map(row =>
                                        <tr key={row.entity}>
                                            <td className="datatable-title">
                                                <a href={decode(row.url)} target="_blank" rel="noreferrer">
                                                    {row.title}
                                                    <Icon.ExternalLink size="16"/>
                                                </a>
                                            </td>
                                            { mobileView || <td className="datatable-description" onClick={() => handleExpandBookmark(row)}>{row.description}</td> }
                                            <td className="datatable-actions">
                                                <AddFavoriteButton bookmark={row} favoritesType={itemType} />
                                                <EditBookmarkButton bookmark={row} />
                                                {removeButton(row)}
                                            </td>
                                        </tr>)
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            {filteredItemsLength >= listIndex && <div style={{display: 'flex', justifyContent: 'center', marginBlockEnd: '24px' }}>
                <AlchemyButton onClick={handleList}>
                    <slot className='d-flex'>
                        {i18n.t("loader:load-more")} {i18n.t("bookmarks")} ({filteredItemsLength - listIndex} {i18n.t("loader:left")})
                        <Icon.ArrowDown />
                    </slot>
                </AlchemyButton>
            </div>}
        </div>
    )
}
